import { Route, Routes, useLocation, Navigate } from "react-router";
import { lazy, Suspense, useCallback, useEffect } from "react";
// Import your components
import { MainLayout } from "@/components/navigation/main-layout";
import { PermissionRoute } from "@/components/navigation/permission-route";
import { useAuthStore } from "@/stores/authStore";
import { LoadingSpinner } from "../ui/loading-spinner";
import { ErrorBoundary } from "react-error-boundary";
import { WebPageEmbed } from "./web-page-embed-props";
// Eager (no lazy): el login es la primera vista, diferirlo solo agrega una
// cascada + spinner de Suspense en cada recarga.
import { LoginWrapper } from "@/pages/menu/Login";
const Home = lazy(() =>
  import("@/pages/menu/Home").then((m) => ({ default: m.Home }))
);
const HomeLite = lazy(() =>
  import("@/pages/menu/HomeLite").then((m) => ({ default: m.HomeLite }))
);

const NotificationsList = lazy(() =>
  import(
    "@/pages/settings/NotificacionesAdmin" /* webpackChunkName: "admin-notifications" */
  ).then((module) => ({ default: module.NotificationsList }))
);

const ConnectionsManagement = lazy(() =>
  import(
    "@/pages/settings/ConnectionsManagement" /* webpackChunkName: "connections-management" */
  ).then((module) => ({ default: module.ConnectionsManagement }))
);
const LandingPage = lazy(() =>
  import("@/pages/menu/LandingPage" /* webpackChunkName: "landing" */).then(
    (module) => ({ default: module.LandingPage })
  )
);
const ReporteConsolidados = lazy(() =>
  import("@/pages/Indicadores/Reportes").then((module) => ({
    default: module.ReporteConsolidados,
  }))
);

const Unauthorized = lazy(() =>
  import("@/pages/menu/Unauthorized" /* webpackChunkName: "auth" */).then(
    (module) => ({ default: module.Unauthorized })
  )
);

const NotFound = lazy(() =>
  import("@/pages/menu/NotFound" /* webpackChunkName: "error-pages" */).then(
    (module) => ({ default: module.NotFound })
  )
);

const Settings = lazy(() =>
  import("@/pages/menu/Settings" /* webpackChunkName: "settings" */).then(
    (module) => ({ default: module.Settings })
  )
);

// Componentes de configuración con chunking agrupado
const SettingsComponents = {
  PanelDeAdministracion: lazy(() =>
    import(
      "@/pages/settings/PanelDeAdministracion" /* webpackChunkName: "admin-panel" */
    ).then((module) => ({ default: module.PanelDeAdministracion }))
  ),
  UsuariosList: lazy(() =>
    import(
      "@/pages/settings/UsuariosList" /* webpackChunkName: "admin-users" */
    ).then((module) => ({ default: module.UsuariosList }))
  ),
  RolesList: lazy(() =>
    import(
      "@/pages/settings/RolesList" /* webpackChunkName: "admin-roles" */
    ).then((module) => ({ default: module.RolesList }))
  ),
  PoliciesList: lazy(() =>
    import(
      "@/pages/settings/PoliciesList" /* webpackChunkName: "admin-policies" */
    ).then((module) => ({ default: module.PoliciesList }))
  ),
  MenusList: lazy(() =>
    import(
      "@/pages/settings/MenusList" /* webpackChunkName: "admin-menus" */
    ).then((module) => ({ default: module.MenusList }))
  ),
  HorariosRepartoList: lazy(() =>
    import(
      "@/pages/llamadas/reglas/HorariosRepartoList" /* webpackChunkName: "horarios-reparto" */
    ).then((module) => ({ default: module.HorariosRepartoList }))
  ),
  DiasNoLaborablesList: lazy(() =>
    import(
      "@/pages/llamadas/reglas/DiasNoLaborablesList" /* webpackChunkName: "dias-no-laborables" */
    ).then((module) => ({ default: module.DiasNoLaborablesList }))
  ),
  VariablesAdmin: lazy(() =>
    import(
      "@/pages/llamadas/reglas/VariablesAdmin" /* webpackChunkName: "variables-admin" */
    ).then((module) => ({ default: module.VariablesAdmin }))
  ),
  FuncionalidadesAdmin: lazy(() =>
    import(
      "@/pages/llamadas/reglas/FuncionalidadesAdmin" /* webpackChunkName: "funcionalidades-admin" */
    ).then((module) => ({ default: module.FuncionalidadesAdmin }))
  ),
  CamposExpuestosAdmin: lazy(() =>
    import(
      "@/pages/llamadas/reglas/CamposExpuestosAdmin" /* webpackChunkName: "campos-expuestos-admin" */
    ).then((module) => ({ default: module.CamposExpuestosAdmin }))
  ),
  RulesAdmin: lazy(() =>
    import(
      "@/pages/llamadas/reglas/RulesAdmin" /* webpackChunkName: "rules-admin" */
    ).then((module) => ({ default: module.RulesAdmin }))
  ),
  DocumentacionesList: lazy(() =>
    import(
      "@/pages/settings/DocumentacionesList" /* webpackChunkName: "admin-docs" */
    ).then((module) => ({ default: module.DocumentacionesList }))
  ),
  ServiciosList: lazy(() =>
    import(
      "@/pages/settings/ServiciosList" /* webpackChunkName: "admin-servicios" */
    ).then((module) => ({ default: module.ServiciosList }))
  ),
  PrestadoresList: lazy(() =>
    import(
      "@/pages/settings/PrestadoresList" /* webpackChunkName: "admin-servicios" */
    ).then((module) => ({ default: module.PrestadoresList }))
  ),
  HorariosCitas: lazy(() =>
    import(
      "@/pages/settings/HorariosCitas" /* webpackChunkName: "admin-servicios" */
    ).then((module) => ({ default: module.HorariosCitas }))
  ),
};
const ReporteCat = lazy(() =>
  import(
    "@/pages/llamadas/ReporteCat" /* webpackChunkName: "reporte-cat" */
  ).then((module) => ({ default: module.ReporteCat }))
);

const NuevoProspecto = lazy(() =>
  import(
    "@/pages/agenda/NuevoProspecto" /* webpackChunkName: "nuevo-prospecto" */
  ).then((module) => ({ default: module.NuevoProspecto }))
);
const ProspectosList = lazy(() =>
  import(
    "@/pages/agenda/ProspectosList" /* webpackChunkName: "prospectos-list" */
  ).then((module) => ({ default: module.ProspectosList }))
);
const ProspectosListLite = lazy(() =>
  import(
    "@/pages/agenda/ProspectosListLite" /* webpackChunkName: "prospectos-list-lite" */
  ).then((module) => ({ default: module.ProspectosListLite }))
);

const CrearCita = lazy(() =>
  import(
    "@/pages/agenda/citapruebas" /* webpackChunkName: "crear-cita" */
  ).then((module) => ({ default: module.CrearCita }))
);

const CrearCitaLite = lazy(() =>
  import(
    "@/pages/agenda/CrearCitaLite" /* webpackChunkName: "crear-cita-lite" */
  ).then((module) => ({ default: module.CrearCitaLite }))
);

const CitasList = lazy(() =>
  import(
    "@/pages/agenda/CitasList" /* webpackChunkName: "citas-list" */
  ).then((module) => ({ default: module.CitasList }))
);
const CitasPacientes = lazy(() =>
  import(
    "@/pages/agenda/CitasPacientes" /* webpackChunkName: "citas-pacientes" */
  ).then((module) => ({ default: module.CitasPacientes }))
);
const AsistenciaCitas = lazy(() =>
  import(
    "@/pages/agenda/AsistenciaCitas" /* webpackChunkName: "asistencia-citas" */
  ).then((module) => ({ default: module.AsistenciaCitas }))
);
const AdminCitas = lazy(() =>
  import(
    "@/pages/agenda/AdminCitas" /* webpackChunkName: "admin-citas" */
  ).then((module) => ({ default: module.AdminCitas }))
);
const CitasListLite = lazy(() =>
  import(
    "@/pages/agenda/CitasListLite" /* webpackChunkName: "citas-list-lite" */
  ).then((module) => ({ default: module.CitasListLite }))
);



import { preloadCriticalComponents } from "@/lib/preloadUtils";
// Lazy: evitar cargar AG Grid/PT2 hasta navegar a la ruta correspondiente
const PT2 = lazy(() =>
  import("@/pages/llamadas/PT2" /* webpackChunkName: "pt2" */).then((m) => ({
    default: m.PT2,
  }))
);

const RecuperacionCAT = lazy(() =>
  import("@/pages/llamadas/RecuperacionCat" /* webpackChunkName: "pt2" */).then(
    (m) => ({
      default: m.RecuperacionCAT,
    })
  )
);

const WhatsApp = lazy(() =>
  import("@/pages/whatsapp/WhatsApp" /* webpackChunkName: "whatsapp" */).then(
    (m) => ({ default: m.WhatsApp })
  )
);

const ChatHubWA = lazy(() =>
  import("@/pages/whatsapp/ChatHubWA" /* webpackChunkName: "chat-hub-wa" */).then(
    (m) => ({ default: m.ChatHubWA })
  )
);

const BlogList = lazy(() =>
  import("@/pages/blog/BlogList" /* webpackChunkName: "blog" */).then((m) => ({
    default: m.BlogList,
  }))
);

const BlogEditor = lazy(() =>
  import("@/pages/blog/BlogEditor" /* webpackChunkName: "blog" */).then((m) => ({
    default: m.BlogEditor,
  }))
);

const BlogCategories = lazy(() =>
  import("@/pages/blog/BlogCategories" /* webpackChunkName: "blog" */).then((m) => ({
    default: m.BlogCategories,
  }))
);

// TEMPORALMENTE DESHABILITADO para excluir su chunk (~2.1 MB) del build.
// Sin referencias, Rollup no lo incluye y no pesa en la carga.
// Reactivar: descomentar este import y el <Route path="editor/:id/content"> de abajo.
// const BlogContentEditor = lazy(() =>
//   import("@/pages/blog/BlogContentEditor" /* webpackChunkName: "blog-content" */).then((m) => ({
//     default: m.BlogContentEditor,
//   }))
// );

const BlogPublicView = lazy(() =>
  import("@/pages/blog/BlogPublicView" /* webpackChunkName: "blog-public" */).then((m) => ({
    default: m.BlogPublicView,
  }))
);


import { getEffectiveAuth } from "@/lib/authUtils";
import { hasLowSpecDevice } from "@/lib/hasLowSpecDevice";

// Lazy: arrastran jsPDF (pdfGenerator) + almacén/finanzas al entry;
// diferirlas las saca de la ruta crítica de /home (mismo <Suspense>).
const PreIntegracionPage = lazy(() =>
  import("@/pages/finanzas/PreintegracionPage" /* webpackChunkName: "finanzas" */).then((m) => ({
    default: m.PreIntegracionPage,
  }))
);
const MovimientosPage = lazy(() =>
  import("@/pages/almacen/MovimientosPage" /* webpackChunkName: "almacen" */).then((m) => ({
    default: m.MovimientosPage,
  }))
);
const ReportesPage = lazy(() =>
  import("@/pages/almacen/ReportesPage" /* webpackChunkName: "almacen" */).then((m) => ({
    default: m.ReportesPage,
  }))
);
const ProductosPage = lazy(() =>
  import("@/pages/almacen/ProductosPage" /* webpackChunkName: "almacen" */).then((m) => ({
    default: m.ProductosPage,
  }))
);
const CambiosProductoPage = lazy(() =>
  import("@/pages/almacen/CambiosProductoPage" /* webpackChunkName: "almacen" */).then((m) => ({
    default: m.CambiosProductoPage,
  }))
);
//import { PreIntegracionPage } from "@/pages/finanzas/preintegracion";


export const AppRoutes = () => {
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const expiresAt = useAuthStore((s) => s.expiresAt);

  // Detección usada por Home/HomeLite y otras rutas
  const isMobile = useCallback(
    () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    if (token) {
      preloadCriticalComponents();
    }
  }, [token]);

  // No prefetch: el Login pesado se carga solo cuando se navega a /login

  return (
    <ErrorBoundary fallback={<div>Error al cargar la página</div>}>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route
            path="/login/*"
            element={
              getEffectiveAuth({ token, expiresAt }).tokenValid ? (
                <Navigate to="/home" replace />
              ) : (
                <LoginWrapper />
              )
            }
          />

          <Route path="/" element={<MainLayout />}>
            <Route
              index
              element={
                isMobile() || hasLowSpecDevice() ? <HomeLite /> : <Home />
              }
            />
            <Route
              path="home"
              element={
                <PermissionRoute
                  element={
                    isMobile() || hasLowSpecDevice() ? <HomeLite /> : <Home />
                  }
                  requiredPermission="puede_ver_home"
                />
              }
            />
  
            <Route path="/reportes">
              <Route
                path="indicadores-cat"
                element={
                  <PermissionRoute
                    element={<ReporteCat />}
                    requiredPermission="puede_ver_indicadores_cat"
                  />
                }
              />
              <Route
                path="Indicadores"
                element={
                  <PermissionRoute
                    element={<ReporteConsolidados />}
                    //requiredPermission="puede_ver_cambio_teradol"
                  />
                }
              />


              <Route
                path="indicadores"
                element={
                  <WebPageEmbed url="http://192.168.4.2:94/CVSArtricenter/Pages/CVSPages/Indicadores.aspx" />
                }
              />
        
            </Route>

            <Route path="/agenda">
              <Route
                path="citas"
                element={
                  <PermissionRoute
                    element={
                      isMobile() || hasLowSpecDevice() ? (
                        <CitasListLite />
                      ) : (
                        <CitasList />
                      )
                    }
                    requiredPermission="puede_ver_citas_v2"
                  />
                }
              />
              <Route
                path="crear-cita"
                element={
                  <PermissionRoute
                    element={
                      isMobile() || hasLowSpecDevice() ? (
                        <CrearCitaLite />
                      ) : (
                        <CrearCita />
                      )
                    }
                    requiredPermission="puede_ver_busqueda_de_prospectos_v2"
                  />
                }
              />
              <Route
                path="citas-pacientes"
                element={
                  /*<PermissionRoute
                    element={<CitasPacientes />}
                    requiredPermission="puede_ver_citas_pacientes"
                  />
                  */
                  <CitasPacientes />
                }
              />
              <Route
                path="pantalla-trabajo"
                element={
                  <PermissionRoute
                    element={<PT2 />}
                    requiredPermission="puede_ver_menu_cat_pantalla_trabajo"
                  />
                }
              />
              <Route
                path="pantalla-trabajo-recuperacion"
                element={
                  <PermissionRoute
                    element={<RecuperacionCAT />}
                    requiredPermission="puede_ver_menu_pantalla_trabajo_recuperacion"
                  />
                }
              />
              <Route
                path="pantalla-trabajo-recuperacion"
                element={
                  <PermissionRoute
                    element={<RecuperacionCAT />}
                    requiredPermission="puede_ver_menu_pantalla_trabajo_recuperacion"
                  />
                }
              />
              <Route
                path="reporte-cat"
                element={
                  <PermissionRoute
                    element={<ReporteCat />}
                    requiredPermission="puede_ver_menu_cat_pantalla_trabajo"
                  />
                }
              />
              <Route
                path="prospecto-nuevo"
                element={
                  <PermissionRoute
                    element={<NuevoProspecto />}
                    requiredPermission="puede_ver_nuevo_prospecto_v2"
                  />
                }
              />
              <Route
                path="prospecto-busqueda"
                element={
                  <PermissionRoute
                    element={
                      isMobile() || hasLowSpecDevice() ? (
                        <ProspectosListLite />
                      ) : (
                        <ProspectosList />
                      )
                    }
                    requiredPermission="puede_ver_busqueda_de_prospectos_v2"
                  />
                }
              />
              {/* De momento libre: sin PermissionRoute, como en el branch de gerardo01. */}
              <Route path="asistencia-citas" element={<AsistenciaCitas />} />
              <Route path="admin-citas" element={<AdminCitas />} />
            </Route>

            <Route path="whatsapp">
              <Route
                index
                element={
                  <PermissionRoute
                    element={<>hola mundo </>}
                    requiredPermission="puede_enviar_whatsapp_de_confirmacion"
                  />
                }
              />
               <Route
                path="plantillas"
                element={
                  <PermissionRoute
                    element={<WhatsApp />}
                    requiredPermission="puede_enviar_whatsapp_de_confirmacion"
                  />
                }
              />
              <Route
                path="chathub"
                element={
              <ChatHubWA />
                }
              />
            </Route>

            <Route path="/settings">
              <Route
                index
                element={
                  <PermissionRoute
                    element={<Settings />}
                    requiredPermission="puede_ver_sistemas"
                  />
                }
              />

              <Route
                path="funcionalidades"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.FuncionalidadesAdmin />}
                    requiredPermission="puede_ver_settings_funcionalidades"
                  />
                }
              />
              <Route
                path="campos-expuestos"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.CamposExpuestosAdmin />}
                    requiredPermission="puede_ver_settings_campos_expuestos"
                  />
                }
              />
              <Route
                path="horarios-reparto"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.HorariosRepartoList />}
                    requiredPermission="puede_ver_settings_horarios_reparto"
                  />
                }
              />
              <Route
                path="dias-no-laborables"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.DiasNoLaborablesList />}
                    requiredPermission="puede_ver_settings_dias_no_laborables"
                  />
                }
              />
              <Route
                path="cat-reglas-reparticion"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.RulesAdmin />}
                    requiredPermission="puede_ver_reglas_de_reparticion"
                  />
                }
              />
              <Route
                path="panel-administracion"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.PanelDeAdministracion />}
                    requiredPermission="puede_ver_sistemas_panel_administracion"
                  />
                }
              />
              <Route
                path="notificaciones"
                element={
                  <PermissionRoute
                    element={<NotificationsList />}
                    requiredPermission="puede_ver_panel_de_notificaciones"
                  />
                }
              />
              <Route
                path="conexiones"
                element={
                  <PermissionRoute
                    element={<ConnectionsManagement />}
                    requiredPermission="puede_ver_panel_de_conexiones"
                  />
                }
              />

              <Route
                path="usuarios"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.UsuariosList />}
                    requiredPermission="puede_ver_usuarios"
                  />
                }
              />
              <Route
                path="roles"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.RolesList />}
                    requiredPermission="puede_ver_roles"
                  />
                }
              />
              <Route
                path="politicas"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.PoliciesList />}
                    requiredPermission="puede_ver_politicas"
                  />
                }
              />
              <Route
                path="menus"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.MenusList />}
                    requiredPermission="puede_ver_menus"
                  />
                }
              />
              <Route
                path="documentaciones"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.DocumentacionesList />}
                    requiredPermission="puede_ver_documentaciones"
                  />
                }
              />
              <Route
                path="servicios"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.ServiciosList />}
                    requiredPermission="puede_ver_servicios"
                  />
                }
              />
              <Route
                path="prestadores"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.PrestadoresList />}
                    requiredPermission="puede_ver_servicios"
                  />
                }
              />
              <Route
                path="horarios-citas"
                element={
                  <PermissionRoute
                    element={<SettingsComponents.HorariosCitas />}
                    requiredPermission="puede_ver_citas_tmi_cvs"
                  />
                }
              />
            </Route>
            <Route path="/blog">
              <Route
                index
                element={
                  <PermissionRoute
                    element={<BlogList />}
                    requiredPermission="blog_admin"
                  />
                }
              />
              <Route
                path="editor"
                element={
                  <PermissionRoute
                    element={<BlogEditor />}
                    requiredPermission="blog_admin"
                  />
                }
              />
              <Route
                path="editor/:id"
                element={
                  <PermissionRoute
                    element={<BlogEditor />}
                    requiredPermission="blog_admin"
                  />
                }
              />
              <Route
                path="categories"
                element={
                  <PermissionRoute
                    element={<BlogCategories />}
                    requiredPermission="blog_admin"
                  />
                }
              />
              {/* TEMPORALMENTE DESHABILITADO: BlogContentEditor excluido del build (chunk ~2.1 MB).
                  Reactivar junto con su import lazy de arriba.
              <Route
                path="editor/:id/content"
                element={
                  <PermissionRoute
                    element={<BlogContentEditor />}
                    requiredPermission="blog_admin"
                  />
                }
              /> */}
              <Route path=":slug" element={<BlogPublicView />} />
            </Route>

          <Route path="/finanzas">
              <Route
                path="preintegracion"
                element={
                  <PermissionRoute
                    element={
                     <PreIntegracionPage />
                    }
                    requiredPermission="puede_ver_preintegracion"
                  />
                }
              />
            </Route>
            <Route path="/almacen">
              <Route
                path="movimientos"
                element={
                  <PermissionRoute
                    element={<MovimientosPage />}
                    requiredPermission="puede_ver_almacen_movimientos"
                  />
                }
              />
              <Route
                path="reportes"
                element={
                  <PermissionRoute
                    element={<ReportesPage />}
                    // TODO: cambiar a permiso de almacén cuando se cree.
                    // Hoy se reutiliza el mismo gate que el resto del modulo.
                    requiredPermission="puede_ver_almacen_movimientos"
                  />
                }
              />
              <Route
                path="productos"
                element={
                  <PermissionRoute
                    element={<ProductosPage />}
                    requiredPermission="puede_ver_almacen_movimientos"
                  />
                }
              />
              <Route
                path="cambios-producto"
                element={
                  <PermissionRoute
                    element={<CambiosProductoPage />}
                    requiredPermission="puede_ver_almacen_movimientos"
                  />
                }
              />
            </Route>
          </Route>
          {/* Ruta 404 - Debe ir al final para capturar cualquier URL que no coincida */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};
